'use client';

import { Button, Container, Stack, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';

import Form from '@/components/Forms/Form';
import FormCheckbox from '@/components/Forms/FormCheckbox';
import FormFileUpload from '@/components/Forms/FormFileUpload';
import FormInput from '@/components/Forms/FormInput';
import OpenLetterCheck from '@/components/SvgIcons/OpenLetterCheck';
import { Link } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';
import useToggleState from '@/utils/hooks/useToggleState';
import { FormValidator } from '@/utils/static/FormValidator';

import styles from './OpenLetterSection.module.scss';

export interface OpenLetterFormValues {
  fullName: string;
  email: string;
  phone: string;
  link: string;
  anotherLink: string;
  cv: File | null;
  coverLetter: File | null;
  consent: boolean;
}

const defaultValues: OpenLetterFormValues = {
  fullName: '',
  email: '',
  phone: '',
  link: '',
  anotherLink: '',
  cv: null,
  coverLetter: null,
  consent: false,
};

const OpenLetterSection = () => {
  const [successState, toggleSuccessState] = useToggleState();
  const t = useTranslations('career');

  const handleSubmit = async (formValues: OpenLetterFormValues) => {
    try {
      const formData = new FormData();

      formData.append('fullName', formValues.fullName);
      formData.append('email', formValues.email);
      formData.append('phone', formValues.phone);
      formData.append('link', formValues.link);
      formData.append('anotherLink', formValues.anotherLink);

      if (formValues.cv) {
        formData.append('cv', formValues.cv);
      }

      if (formValues.coverLetter) {
        formData.append('coverLetter', formValues.coverLetter);
      }

      const response = await fetch('/api/open-letter', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Error sending open letter');
      }

      toggleSuccessState();
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('An error occurred:', error);
      throw error;
    }
  };

  const renderForm = () => (
    <Stack alignItems="center" className={styles.formWrapper}>
      <Form key="form" defaultValues={defaultValues} onSubmit={handleSubmit} mode="onBlur" className={styles.form}>
        {({ formState: { isSubmitting } }) => (
          <Stack gap={3} width="100%">
            <Typography variant="h4" component="h2">
              {t('openLetter.title')}
            </Typography>
            <FormInput name="fullName" label={t('openLetter.name')} validate={FormValidator.isNotEmpty} />
            <FormInput
              name="email"
              type="email"
              label={t('openLetter.email')}
              validate={FormValidator.all(FormValidator.isNotEmpty, FormValidator.isValidEmail)}
            />
            <FormInput name="phone" label={t('openLetter.phone')} validate={FormValidator.isNotEmpty} />
            <FormInput name="link" label={t('openLetter.link')} validate={FormValidator.isNotEmpty} />
            <FormInput name="anotherLink" label={t('openLetter.anotherLink')} />
            <Stack gap={2}>
              <Typography variant="body1">{t('openLetter.filesLabel')}</Typography>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <FormFileUpload name="cv" label={t('openLetter.uploadCV')} accept=".pdf,.doc,.docx" maxSize={5} />
                <FormFileUpload
                  name="coverLetter"
                  label={t('openLetter.uploadCoverLetter')}
                  accept=".pdf,.doc,.docx"
                  maxSize={5}
                />
              </Stack>
            </Stack>
            <FormCheckbox name="consent" label={t('openLetter.consent')} validate={FormValidator.isChecked} />
            <Button type="submit" size="large" disabled={isSubmitting} fullWidth>
              {isSubmitting ? t('openLetter.sending') : t('openLetter.submit')}
            </Button>
          </Stack>
        )}
      </Form>
    </Stack>
  );

  const renderSuccessState = () => (
    <Stack alignItems="center" className={styles.formWrapper}>
      <Stack key="success" justifyContent="center" alignItems="center" className={styles.successBox}>
        <Stack alignItems="center" className={styles.iconWrapper}>
          <OpenLetterCheck fill={colors.blue500} size={52} />
        </Stack>
        <Typography variant="h3" component="p" textAlign="center" color={colors.white}>
          {t('successState.title')}
        </Typography>
        <Typography variant="body1" color={colors.graytone300} textAlign="center">
          {t('successState.text')}
        </Typography>
        <Link href="/">
          <Button size="large" fullWidth>
            {t('successState.button')}
          </Button>
        </Link>
      </Stack>
    </Stack>
  );

  return (
    <Container component="section" maxWidth="xl" className={styles.container}>
      {successState ? renderSuccessState() : renderForm()}
    </Container>
  );
};

export default OpenLetterSection;
